import { createContext, lazy, Suspense, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { BrowserAuthSession } from '../../../shared/api/models'
import { authExpiredEvent, authUserKey, clearBrowserAuth, lastAuthUser, loadBrowserAuth } from '../lib/browserAuth'
import { setApiWorkspaceId } from '../api/workspaceState'
import { getInitData } from '../telegram'
const AuthScreen = lazy(() => import('../components/layout/AuthScreen'))

const AuthContext = createContext<BrowserAuthSession | null>(null)
// eslint-disable-next-line react-refresh/only-export-components -- provider and hook share their contract
export function useAuth() { return useContext(AuthContext) }

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<BrowserAuthSession | null>(null)
  const [error, setError] = useState('')
  const [online, setOnline] = useState(() => navigator.onLine)
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)
  const identity = useRef(lastAuthUser())
  useEffect(() => {
    const expired = () => { clearBrowserAuth(); setApiWorkspaceId(null); setSession(null); setLoading(true); setAttempt(n => n + 1) }
    const connected = () => { setOnline(true); setLoading(true); setAttempt(n => n + 1) }
    const disconnected = () => { setOnline(false); clearBrowserAuth(); setSession(current => current?.authenticated ? null : current) }
    const visible = () => { if (document.visibilityState === 'visible') setAttempt(n => n + 1) }
    window.addEventListener(authExpiredEvent, expired)
    window.addEventListener('online', connected); window.addEventListener('offline', disconnected)
    document.addEventListener('visibilitychange', visible)
    // Other tabs immediately hide financial state after logout/account changes.
    const changed = (event: StorageEvent) => { if (!getInitData() && event.key === authUserKey) expired() }
    window.addEventListener('storage', changed)
    return () => { window.removeEventListener(authExpiredEvent, expired); window.removeEventListener('online', connected)
      window.removeEventListener('offline', disconnected); document.removeEventListener('visibilitychange', visible); window.removeEventListener('storage', changed) }
  }, [])
  useEffect(() => {
    if (!online) return
    const controller = new AbortController()
    void loadBrowserAuth(controller.signal).then(result => {
      if (identity.current !== (result.user?.id || null)) setApiWorkspaceId(null)
      identity.current = result.user?.id || null
      setSession(result)
      setError(''); setLoading(false)
    }).catch((cause: unknown) => {
      if (controller.signal.aborted) return
      clearBrowserAuth(); setSession(current => current?.authenticated ? null : current); setError(cause instanceof Error ? cause.message : 'auth_unavailable'); setLoading(false)
    })
    return () => controller.abort()
  }, [attempt, online])
  // Revalidate a completed login even if iOS delivered the callback to Safari.
  useEffect(() => {
    if (!online || session?.authenticated || !session?.loginPending || getInitData()) return
    const id = window.setInterval(() => setAttempt(n => n + 1), 5000)
    return () => window.clearInterval(id)
  }, [online, session?.authenticated, session?.loginPending])

  if (online && session?.authenticated) return <AuthContext.Provider key={session.user?.id} value={session}>{children}</AuthContext.Provider>
  return <Suspense fallback={<div role="status" className="p-6 text-sm text-muted-foreground">Проверяем вход…</div>}><AuthScreen session={session} error={error} online={online} loading={loading} retry={() => { setOnline(navigator.onLine); setLoading(true); setAttempt(n => n + 1) }} /></Suspense>
}
